import React, { useState } from 'react';
import { 
  Eye, 
  Database, 
  Shield, 
  Zap, 
  ArrowRight, 
  Activity, 
  Layers,
  AlertCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import logo from '../../../assets/owl.png'
import Header from './Header';
import { loginRequest } from "../../../utils/auth-config";
import { useMsal } from '@azure/msal-react';

export default function OwlsightComponent() {
  const { instance } = useMsal();
  const [activeFeature, setActiveFeature] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

    const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((e) => {
      console.error(e);
    });
  };

  // Data quality metrics for display
  const qualityMetrics = [
    { name: "Completeness", value: 96, color: "blue" },
    { name: "Consistency", value: 98, color: "purple" },
    { name: "Accuracy", value: 94, color: "indigo" },
    { name: "Timeliness", value: 99, color: "teal" },
    { name: "Uniqueness", value: 97, color: "blue" },
    { name: "Validity", value: 95, color: "purple" },
    { name: "Integrity", value: 92, color: "indigo" }
  ];

  const keyFeatures = [
    {
      icon: <Database className="text-blue-400" size={32} />,
      title: "Rule Engine",
      description: "Define custom or pre-built validation rules using SQL, Python, or GUI-based logic"
    },
    {
      icon: <Activity className="text-purple-400" size={32} />,
      title: "Anomaly Detection",
      description: "ML-driven profiling to detect drift, spikes, and schema evolution"
    },
    {
      icon: <AlertCircle className="text-teal-400" size={32} />,
      title: "Real-Time Alerts",
      description: "Integrate with Slack, Teams, PagerDuty, or email"
    },
    {
      icon: <Zap className="text-green-400" size={32} />,
      title: "Auto-Profiling",
      description: "Automatically profile new datasets upon ingestion"
    }
  ];

  return (
             <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-900 min-h-screen text-white overflow-hidden">
              <Header />
<div
  className="relative bg-gradient-to-b from-transparent via-blue-900 to-blue-900 min-h-screen text-white overflow-hidden"
  style={{ backgroundColor: 'rgba(58, 89, 209, 0.8)' }}
>

      {/* Animated Background Particles */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-indigo-500"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 5 + 2}px`,
              height: `${Math.random() * 5 + 2}px`,
              opacity: Math.random() * 0.5,
              animation: `float ${Math.random() * 20 + 10}s linear infinite`,
              animationDelay: `${Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-20 relative">

        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-block relative mb-6">
              <div className="center">
          <img 
            src={logo}
            alt="Owl sight logo" 
  className="w-[250px] h-[250px] object-contain"
          />
        </div>
          </div>
          {/* <h1 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 via-purple-300 to-blue-300 mb-4">Owlsight</h1> */}
          <p className="text-xl text-indigo-200 max-w-3xl mx-auto">
            Intelligent Data Quality for the AI Era
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-12">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'metrics', label: 'Quality Metrics' },
            { key: 'features', label: 'Key Features' }
          ].map((tab) => (
            <button
              key={tab.key}
              className={`
                px-6 py-3 mx-2 rounded-lg transition-all duration-300
                ${activeSection === tab.key 
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' 
                  : 'bg-gray-800 text-indigo-200 hover:bg-gray-700'}
              `}
              onClick={() => setActiveSection(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        {activeSection === 'overview' && (
          <div className="bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-indigo-500 border-opacity-30">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent">
              Enterprise-Grade Data Quality Monitoring
            </h2>
            <p className="text-indigo-200 mb-4">
              <span className="font-semibold">Owlsight</span> is an enterprise-grade <span className="text-indigo-300">data quality monitoring platform</span> built to bring visibility, trust, and control to your most critical asset — your data. Designed to support modern data ecosystems, Owlsight empowers organizations to detect, diagnose, and prevent data quality issues <span className="text-indigo-300">across all dimensions</span> — at scale, in real time, and with surgical precision.
            </p>
            
            <div className="bg-gray-900 bg-opacity-60 p-6 rounded-xl mt-8">
              <h3 className="text-xl font-bold mb-4 text-indigo-300">Why Owlsight?</h3>
              <p className="text-indigo-200 mb-4">
                Today's data pipelines are distributed, multi-format, and high-velocity. From operational databases and data lakes to cloud warehouses and APIs, businesses rely on thousands of data streams to drive decision-making, automation, and customer experience.
              </p>
              <p className="text-indigo-200 mb-4">
                But bad data remains the silent killer.
              </p>
              <p className="text-indigo-300 font-medium">
                <span className="font-semibold">Owlsight</span> provides continuous, intelligent surveillance over your data, giving you the power to proactively eliminate blind spots and data decay before it impacts your business.
              </p>
            </div>

            <div className="space-y-4 mt-8">
              <div className="flex items-center">
                <Zap className="text-indigo-400 mr-3" size={24} />
                <span className="text-indigo-200">
                  Automated Data Analysis: Uncover patterns and issues across large volumes of enterprise data
                </span>
              </div>
              <div className="flex items-center">
                <Database className="text-purple-400 mr-3" size={24} />
                <span className="text-indigo-200">
                  Intelligent Data Monitoring: Automate quality checks based on content, context, and metadata
                </span>
              </div>
              <div className="flex items-center">
                <Shield className="text-green-400 mr-3" size={24} />
                <span className="text-indigo-200">
                  Enhanced Data Protection: Prevent quality issues from propagating downstream
                </span>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'metrics' && (
          <div className="bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-indigo-500 border-opacity-30">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent">
              Full-Spectrum Data Quality Coverage
            </h2>
            <p className="text-indigo-200 mb-8">
              Owlsight monitors <span className="font-semibold">all key dimensions of data quality</span> in a <span className="text-indigo-300">unified quality scorecard</span>, with drill-down capabilities for business and technical users.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {qualityMetrics.map((metric, index) => (
                <div 
                  key={index} 
                  className="bg-gray-900 bg-opacity-60 rounded-xl overflow-hidden"
                >
                  <div className="p-4 flex justify-between items-center">
                    <h3 className="font-medium text-indigo-200">{metric.name}</h3>
                    <div className="text-lg font-bold text-indigo-300">{metric.value}%</div>
                  </div>
                  <div className="w-full h-2 bg-gray-800">
                    <div 
                      className={`h-full bg-gradient-to-r from-${metric.color}-500 to-${metric.color}-400`}
                      style={{ width: `${metric.value}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 bg-gray-900 bg-opacity-60 p-6 rounded-xl">
              <h3 className="text-xl font-bold mb-4 text-indigo-300">Quality Dimensions Explained</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <CheckCircle className="text-indigo-400 mt-1 mr-3 flex-shrink-0" size={18} />
                  <div>
                    <span className="font-medium text-indigo-200">Completeness:</span>
                    <p className="text-indigo-200 text-sm">Identify missing values and schema gaps</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-indigo-400 mt-1 mr-3 flex-shrink-0" size={18} />
                  <div>
                    <span className="font-medium text-indigo-200">Consistency:</span>
                    <p className="text-indigo-200 text-sm">Catch format mismatches and cross-source discrepancies</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-indigo-400 mt-1 mr-3 flex-shrink-0" size={18} />
                  <div>
                    <span className="font-medium text-indigo-200">Accuracy:</span>
                    <p className="text-indigo-200 text-sm">Validate values against business rules and ML models</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-indigo-400 mt-1 mr-3 flex-shrink-0" size={18} />
                  <div>
                    <span className="font-medium text-indigo-200">Timeliness:</span>
                    <p className="text-indigo-200 text-sm">Ensure data arrives when and where it's needed</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-indigo-400 mt-1 mr-3 flex-shrink-0" size={18} />
                  <div>
                    <span className="font-medium text-indigo-200">Uniqueness:</span>
                    <p className="text-indigo-200 text-sm">Detect and de-duplicate records with precision</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-indigo-400 mt-1 mr-3 flex-shrink-0" size={18} />
                  <div>
                    <span className="font-medium text-indigo-200">Validity:</span>
                    <p className="text-indigo-200 text-sm">Enforce data type rules, regex, and thresholds</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-indigo-400 mt-1 mr-3 flex-shrink-0" size={18} />
                  <div>
                    <span className="font-medium text-indigo-200">Integrity:</span>
                    <p className="text-indigo-200 text-sm">Verify relational and referential constraints across systems</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'features' && (
          <div>
            <div className="grid md:grid-cols-2 gap-6">
              {keyFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className={`
                    p-6 rounded-2xl transition-all duration-300 
                    ${activeFeature === index 
                      ? 'bg-gray-700 border-2 border-indigo-500 shadow-2xl' 
                      : 'bg-gray-800 bg-opacity-50 border border-transparent'}
                  `}
                  onMouseEnter={() => setActiveFeature(index)}
                  onMouseLeave={() => setActiveFeature(null)}
                >
                  <div className="flex items-center mb-4">
                    {feature.icon}
                    <h3 className="ml-4 text-xl font-semibold text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-indigo-200">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <div 
                className="p-6 rounded-2xl transition-all duration-300 bg-gray-800 bg-opacity-50 border border-transparent"
                onMouseEnter={() => setActiveFeature(4)}
                onMouseLeave={() => setActiveFeature(null)}
              >
                <div className="flex items-center mb-4">
                  <Eye className="text-blue-400" size={32} />
                  <h3 className="ml-4 text-xl font-semibold text-white">
                    Lineage-Aware Monitoring
                  </h3>
                </div>
                <p className="text-indigo-200">
                  Trace data quality issues upstream and downstream across your data ecosystem
                </p>
              </div>
              
              <div 
                className="p-6 rounded-2xl transition-all duration-300 bg-gray-800 bg-opacity-50 border border-transparent"
                onMouseEnter={() => setActiveFeature(5)}
                onMouseLeave={() => setActiveFeature(null)}
              >
                <div className="flex items-center mb-4">
                  <Layers className="text-purple-400" size={32} />
                  <h3 className="ml-4 text-xl font-semibold text-white">
                    Multi-Source Coverage
                  </h3>
                </div>
                <p className="text-indigo-200">
                  Works across Snowflake, BigQuery, SAP, Oracle, AWS, Databricks, and more
                </p>
              </div>
            </div>
            
            <div className="bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-indigo-500 border-opacity-30 mt-8">
              <h3 className="text-xl font-bold mb-4 text-indigo-300 flex items-center">
                <Shield className="mr-2" size={24} />
                Secure by Design, Scalable by Default
              </h3>
              <p className="text-indigo-200">
                Owlsight is built with <span className="font-semibold">enterprise-grade security</span>: role-based access, audit logging, encryption at rest and in transit, and support for private deployments in VPCs or air-gapped environments. It scales to monitor <span className="text-indigo-300">billions of records per day</span>, across hundreds of pipelines and domains.
              </p>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent">
            Elevate Your Data Quality Today
          </h3>
          <p className="text-indigo-200 max-w-2xl mx-auto mb-8">
            Ready to transform your data quality monitoring? Let Owlsight empower your organization with intelligent, real-time data quality surveillance.
          </p>
          <button onClick={handleLogin} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 flex items-center justify-center mx-auto group">
            Request a Demo
            <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={20} />
          </button>
        </div>
      </div>
      
      {/* Footer */}
      <div className="border-t border-indigo-500/20 bg-gray-900/70 py-4">
        <div className="max-w-6xl mx-auto px-4 flex justify-between items-center text-sm text-indigo-200/60">
          <div className="flex items-center gap-1">
            <Clock size={14} />
            Real-time monitoring active
          </div>
          <div>Owlsight v2.5.1</div>
        </div>
      </div>
    </div>
    </div>
  );
}